import { useState } from 'react';
import {
  ATTENDANCE_EVENT_KINDS, ATTENDANCE_RECORD_PATH, Size, attendanceEventProblems, attendanceKindLabel, formatDateOnly, t,
  type ApprovalDay, type AttendanceEventDraft, type AttendanceEventKind,
} from '@bms2/shared';
import { NumericInput, RadioGroup, Textarea } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { useDateFormat } from '../../shell/ViewerZone';
import { post } from '../records/api';
import { line } from '../settings/SettingsShell';
import styles from './HrPages.module.css';

/**
 * Record Attendance on Time Awaiting Approval (piece 34; decision 12851;
 * T3857): Late with its minutes, No Show or Call-Off, and a reason, on the
 * worker's day being approved. 🔒 Drawn through the Form frame; the event is
 * appended to the person's file and never edited, and a correction is a new one.
 */
export function RecordAttendanceWindow({ apiBaseUrl = '', day, onClose, onRecorded }: {
  apiBaseUrl?: string;
  day: Pick<ApprovalDay, 'employmentId' | 'name' | 'workDate'>;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const format = useDateFormat();
  const [draft, setDraft] = useState<AttendanceEventDraft>({ employmentId: day.employmentId, workDate: day.workDate, kind: '', minutesLate: '', reason: '' });
  const [typed, setTyped] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const change = (next: AttendanceEventDraft) => {
    setTyped(true);
    setDraft(next);
  };
  const problems = attendanceEventProblems(draft);
  const save = async () => {
    const answer = await post<{ attendanceEventId: string }>(ATTENDANCE_RECORD_PATH, draft, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onRecorded();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.attendance.title')}
      subtitle={line(day.name, '·', formatDateOnly(`${day.workDate}T00:00:00.000Z`, format))}
      dataForm="record-attendance"
      typed={typed}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.attendance.act'), onAct: () => save(), disabled: problems.length > 0 || !typed, disabledReason: problems[0] ?? '' }}
    >
      <div className={styles.fields} data-record-attendance={`${day.employmentId}|${day.workDate}`}>
        <p className={styles.meta}>{t('hr.attendance.lead')}</p>
        <RadioGroup
          name="attendanceKind"
          legend={t('hr.attendance.kind')}
          orientation="horizontal"
          value={draft.kind || null}
          options={ATTENDANCE_EVENT_KINDS.map((value) => ({ value, label: attendanceKindLabel(value) }))}
          // 🔨 Minutes belong to a Late event alone, so another kind clears them.
          onChange={(value) => change({ ...draft, kind: value as AttendanceEventKind, minutesLate: value === 'Late' ? draft.minutesLate : '' })}
        />
        {draft.kind === 'Late' ? (
          <NumericInput size={Size.Small} kind="number" decimals={0} label={t('hr.attendance.minutesLate')} value={draft.minutesLate === '' ? null : Number(draft.minutesLate)} onChange={(minutes) => change({ ...draft, minutesLate: minutes === null ? '' : String(minutes) })} required name="attendanceMinutes" />
        ) : null}
        <Textarea size={Size.Large} label={t('hr.attendance.reason')} value={draft.reason} onChange={(reason) => change({ ...draft, reason })} placeholder={t('hr.attendance.reasonHint')} required name="attendanceReason" />
      </div>
    </FormFrame>
  );
}
